// Dashboard.jsx - Complete Enhanced Analytics Dashboard
import React, { useState, useEffect, useCallback } from "react";
import {
  Activity,
  Users,
  BarChart3,
  Smartphone,
  MapPin,
  Calendar,
  Clock,
  TrendingUp,
  Eye,
  Zap,
  LayoutDashboard,
  HardDrive,
  RefreshCw,
  X,
  ShoppingCart,
  DollarSign,
  Package,
  CreditCard,
  User,
  ArrowRight,
  TrendingDown,
  Monitor,
  Wifi,
  Battery,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileJson,
  MapPinIcon,
  FlaskConical,
} from "lucide-react";
import UserJourneyModal from "./UserJourneyModal";
import EventsGeoMapView from "./EventsGeoMapView";
import LabBookingAnalytics from "./LabBookingAnalytics";

const API_BASE = "https://api.medstown.com/api/events";

const convertToCSV = (data, headers) => {
  const csvRows = [];
  csvRows.push(headers.join(","));

  data.forEach((row) => {
    const values = headers.map((header) => {
      const value = row[header] !== undefined ? row[header] : "";
      const escaped = ("" + value).replace(/"/g, '\\"');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
};

const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const getFormattedDate = () => {
  const now = new Date();
  return now.toISOString().split("T")[0];
};

const exportOverviewData = (data, range) => {
  return {
    metadata: {
      exportDate: new Date().toISOString(),
      dateRange: data.analytics?.dateRange || {},
      reportPeriod: range,
    },
    metrics: data.analytics?.metrics || {},
    eventsSeries: data.eventsSeries || [],
    topScreens: data.analytics?.screenAnalytics || [],
    eventsByType: data.eventsByType || [],
    topLocations: data.topLocations || [],
    devices: data.devices || [],
  };
};

const exportUserActivityData = (users) => {
  return users.map((user) => ({
    userId: user.userId,
    fullName: user.fullName || "Anonymous",
    phone: user.phone || "N/A",
    totalEvents: user.eventCount,
    screenViews: user.screenViews || 0,
    productViews: user.productViews || 0,
    uniqueScreens: user.screenCount,
    sessionCount: user.sessionCount || 0,
    pincode: user.location?.pincode || "N/A",
    city: user.location?.city || "N/A",
    state: user.location?.state || "N/A",
    lastActive: new Date(user.lastActive).toISOString(),
    lastActiveFormatted: new Date(user.lastActive).toLocaleString(),
  }));
};

const exportEcommerceData = (ecommerce) => {
  return {
    summary: {
      totalCarts: ecommerce?.cart?.totalCarts || 0,
      totalPayments: ecommerce?.payment?.totalPayments || 0,
      totalAmount: ecommerce?.payment?.totalAmount || 0,
      conversionRate: ecommerce?.conversionRate || 0,
    },
    topProducts: (ecommerce?.topProducts || []).map((product) => ({
      productId: product.product_id,
      productName: product.product_name,
      type: product.type,
      views: product.views,
      uniqueUsers: product.uniqueUsers,
    })),
  };
};

const exportScreenUsersData = (screenUsers, screenName) => {
  return screenUsers.map((user) => ({
    screen: screenName,
    userId: user.userId,
    fullName: user.fullName || "Anonymous",
    phone: user.phone || "N/A",
    visitCount: user.visitCount,
    firstVisit: new Date(user.firstVisit).toISOString(),
    lastVisit: new Date(user.lastVisit).toISOString(),
    firstVisitFormatted: new Date(user.firstVisit).toLocaleString(),
    lastVisitFormatted: new Date(user.lastVisit).toLocaleString(),
  }));
};

const handleExportJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  downloadFile(
    jsonString,
    `${filename}_${getFormattedDate()}.json`,
    "application/json"
  );
};

const handleExportCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = convertToCSV(data, headers);
  downloadFile(csvContent, `${filename}_${getFormattedDate()}.csv`, "text/csv");
};

// ============================================================================
// EXPORT BUTTON COMPONENT
// ============================================================================

const ExportButton = ({
  onExportCSV,
  onExportJSON,
  label = "Export",
  disabled = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => !disabled && setShowMenu(!showMenu)}
        disabled={disabled}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 20px",
          background: disabled
            ? "#e2e8f0"
            : "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
          color: disabled ? "#a0aec0" : "#fff",
          border: "none",
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: "600",
          cursor: disabled ? "not-allowed" : "pointer",
          boxShadow: disabled ? "none" : "0 4px 12px rgba(72, 187, 120, 0.3)",
          transition: "all 0.3s",
        }}
        onMouseOver={(e) => {
          if (!disabled) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 6px 16px rgba(72, 187, 120, 0.4)";
          }
        }}
        onMouseOut={(e) => {
          if (!disabled) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(72, 187, 120, 0.3)";
          }
        }}
      >
        <Download size={16} />
        {label}
      </button>

      {showMenu && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setShowMenu(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "8px",
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
              padding: "8px",
              minWidth: "200px",
              zIndex: 1000,
              border: "1px solid #e2e8f0",
            }}
          >
            <button
              onClick={() => {
                onExportCSV();
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2d3748",
                transition: "background-color 0.2s",
                textAlign: "left",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#f7fafc")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <FileSpreadsheet size={18} color="#48bb78" />
              Export as CSV
            </button>
            <button
              onClick={() => {
                onExportJSON();
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2d3748",
                transition: "background-color 0.2s",
                textAlign: "left",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#f7fafc")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <FileJson size={18} color="#667eea" />
              Export as JSON
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [range, setRange] = useState("thisWeek");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customDates, setCustomDates] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // User Journey State
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserJourney, setShowUserJourney] = useState(false);

  // User Activity Filters and Sort State
  const [userFilters, setUserFilters] = useState({
    search: "",
    screen: "",
    sortBy: "eventCount",
    sortOrder: "desc",
    minEvents: "",
    maxEvents: "",
    minScreenViews: "",
    minProductViews: "",
    page: 1,
    limit: 50,
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [pagination, setPagination] = useState(null);

  const [data, setData] = useState({
    analytics: null,
    eventsSeries: [],
    screenCount: null,
    eventsByType: [],
    topLocations: [],
    devices: [],
    userActivity: [],
    ecommerce: null,
    locationMap: [],
  });

  // Fetch User Activity with Filters
  const fetchUserActivity = async (baseParams) => {
    const params = new URLSearchParams(baseParams);

    if (userFilters.search) params.append("search", userFilters.search);
    if (userFilters.screen) params.append("screen", userFilters.screen);
    if (userFilters.sortBy) params.append("sortBy", userFilters.sortBy);
    if (userFilters.sortOrder)
      params.append("sortOrder", userFilters.sortOrder);
    if (userFilters.minEvents)
      params.append("minEvents", userFilters.minEvents);
    if (userFilters.maxEvents)
      params.append("maxEvents", userFilters.maxEvents);
    if (userFilters.minScreenViews)
      params.append("minScreenViews", userFilters.minScreenViews);
    if (userFilters.minProductViews)
      params.append("minProductViews", userFilters.minProductViews);
    params.append("page", userFilters.page);
    params.append("limit", userFilters.limit);

    return fetch(`${API_BASE}/users/activity?${params}`).then((r) => r.json());
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      let params;

      if (range === "custom" && customDates.from && customDates.to) {
        params = new URLSearchParams({
          range: "custom",
          from: customDates.from,
          to: customDates.to,
        });
      } else {
        params = new URLSearchParams({ range });
      }

      const [
        analytics,
        counts,
        screens,
        types,
        locations,
        devices,
        userActivity,
        ecommerce,
        locationMap,
      ] = await Promise.all([
        fetch(`${API_BASE}/analytics?${params}`).then((r) => r.json()),
        fetch(`${API_BASE}/counts?${params}`).then((r) => r.json()),
        fetch(`${API_BASE}/screens?${params}`).then((r) => r.json()),
        fetch(`${API_BASE}/by-type`).then((r) => r.json()),
        fetch(`${API_BASE}/geo/top?${params}`).then((r) => r.json()),
        fetch(`${API_BASE}/devices?${params}`).then((r) => r.json()),
        fetchUserActivity(params),
        fetch(`${API_BASE}/ecommerce?${params}`).then((r) => r.json()),
        fetch(`${API_BASE}/geo/map?${params}`).then((r) => r.json()),
      ]);

      setData({
        analytics: analytics.data || analytics,
        eventsSeries: Array.isArray(counts.data)
          ? counts.data
          : Array.isArray(counts)
          ? counts
          : [],
        screenCount: screens.data || screens,
        eventsByType: Array.isArray(types.data)
          ? types.data
          : Array.isArray(types)
          ? types
          : [],
        topLocations: Array.isArray(locations.data)
          ? locations.data
          : Array.isArray(locations)
          ? locations
          : [],
        devices: Array.isArray(devices.data)
          ? devices.data
          : Array.isArray(devices)
          ? devices
          : [],
        userActivity: Array.isArray(userActivity.data.data)
          ? userActivity.data.data
          : Array.isArray(userActivity)
          ? userActivity
          : [],
        ecommerce: ecommerce.data || ecommerce,
        locationMap: Array.isArray(locationMap.data)
          ? locationMap.data
          : Array.isArray(locationMap)
          ? locationMap
          : [],
      });

      if (userActivity.data.pagination) {
        setPagination(userActivity.data.pagination);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  }, [range, customDates, userFilters]);

  useEffect(() => {
    if (range !== "custom" || (customDates.from && customDates.to)) {
      fetchAllData();
    }
  }, [range, customDates, fetchAllData]);

  const handleFilterChange = (key, value) => {
    setUserFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setUserFilters((prev) => ({ ...prev, page: newPage }));
  };

  const resetFilters = () => {
    setUserFilters({
      search: "",
      screen: "",
      sortBy: "eventCount",
      sortOrder: "desc",
      minEvents: "",
      maxEvents: "",
      minScreenViews: "",
      minProductViews: "",
      page: 1,
      limit: 50,
    });
  };

  const handleRangeChange = (newRange) => {
    if (newRange === "custom") {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
    }
    setRange(newRange);
  };

  const applyCustomRange = () => {
    if (customDates.from && customDates.to) {
      setRange("custom");
      setShowCustomPicker(false);
      fetchAllData();
    }
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
    setShowUserJourney(true);
  };

  const closeUserJourney = () => {
    setShowUserJourney(false);
    setSelectedUserId(null);
  };

  const maxValue = data.eventsSeries.reduce(
    (max, item) => Math.max(max, item.count || 0),
    100
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {/* HEADER */}
        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "24px",
              marginBottom: "24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div>
                <h1
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: "#1a202c",
                    margin: "0 0 4px 0",
                  }}
                >
                  Medstown Analytics
                </h1>
                <p style={{ fontSize: "14px", color: "#718096", margin: 0 }}>
                  {data.analytics?.dateRange?.period || "Loading..."} •
                  Real-time insights
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={fetchAllData}
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  transition: "all 0.3s",
                }}
              >
                <RefreshCw size={16} className={loading ? "spin" : ""} />
                Refresh
              </button>

              <ExportButton
                label="Export Data"
                disabled={loading}
                onExportCSV={() => {
                  if (activeTab === "overview") {
                    console.log(data.eventsSeries);
                    const eventsData = data.eventsSeries.map((item) => ({
                      date: item.date,
                      count: item.count,
                      uniqueUsers: item.uniqueUsers,
                      changePercent: item.changePercent,
                    }));
                    handleExportCSV(eventsData, "medstown_overview_events");
                  } else if (activeTab === "users") {
                    const exportData = exportUserActivityData(
                      data.userActivity
                    );
                    handleExportCSV(exportData, "medstown_users");
                  } else if (activeTab === "ecommerce") {
                    const ecomData = exportEcommerceData(data.ecommerce);
                    handleExportCSV(
                      ecomData.topProducts,
                      "medstown_ecommerce_products"
                    );
                  }
                }}
                onExportJSON={() => {
                  if (activeTab === "overview") {
                    const exportData = exportOverviewData(data, range);
                    handleExportJSON(exportData, "medstown_overview");
                  } else if (activeTab === "users") {
                    const exportData = exportUserActivityData(
                      data.userActivity
                    );
                    handleExportJSON(exportData, "medstown_users");
                  } else if (activeTab === "ecommerce") {
                    const ecomData = exportEcommerceData(data.ecommerce);
                    handleExportJSON(ecomData, "medstown_ecommerce");
                  }
                }}
              />
            </div>
          </div>

          {/* DATE RANGE SELECTOR */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              padding: "8px",
              backgroundColor: "#fff",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          >
            {[
              { id: "today", label: "Today" },
              { id: "yesterday", label: "Yesterday" },
              { id: "thisWeek", label: "This Week" },
              { id: "lastWeek", label: "Last Week" },
              { id: "thisMonth", label: "This Month" },
              { id: "lastMonth", label: "Last Month" },
              { id: "thisYear", label: "This Year" },
              { id: "lastYear", label: "Last Year" },
              { id: "custom", label: "Custom" },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleRangeChange(id)}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backgroundColor: range === id ? "#667eea" : "transparent",
                  color: range === id ? "#fff" : "#4a5568",
                  boxShadow:
                    range === id
                      ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                      : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* CUSTOM DATE PICKER MODAL */}
          {showCustomPicker && (
            <CustomDatePicker
              customDates={customDates}
              setCustomDates={setCustomDates}
              onClose={() => setShowCustomPicker(false)}
              onApply={applyCustomRange}
            />
          )}

          {/* DATE INFO */}
          {data.analytics?.dateRange && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: "#2d3748",
                }}
              >
                <Clock size={16} color="#667eea" />
                <strong>{data.analytics.dateRange.period}:</strong>
                <span>
                  {new Date(
                    data.analytics.dateRange.startDate
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  {" → "}
                  {new Date(
                    data.analytics.dateRange.endDate
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* TAB NAVIGATION */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "32px",
            backgroundColor: "#fff",
            padding: "8px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflowX: "auto",
          }}
        >
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "users", label: "Users", icon: Users },
            { id: "ecommerce", label: "Order Medicines", icon: ShoppingCart },
            { id: "labBooking", label: "Lab Booking", icon: FlaskConical },
            { id: "screens", label: "Screens", icon: Monitor },
            { id: "map", label: "Map", icon: MapPinIcon },
          
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                backgroundColor: activeTab === id ? "#667eea" : "transparent",
                color: activeTab === id ? "#fff" : "#4a5568",
                boxShadow:
                  activeTab === id
                    ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                    : "none",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === "overview" && (
          <OverviewTab data={data} loading={loading} maxValue={maxValue} />
        )}

        {/* USERS TAB - ENHANCED */}
        {activeTab === "users" && (
          <EnhancedUserActivityTable
            users={data.userActivity}
            loading={loading}
            onUserClick={handleUserClick}
            filters={userFilters}
            onFilterChange={handleFilterChange}
            onResetFilters={resetFilters}
            pagination={pagination}
            onPageChange={handlePageChange}
            showFilterPanel={showFilterPanel}
            setShowFilterPanel={setShowFilterPanel}
          />
        )}

        {/* E-COMMERCE TAB */}
        {activeTab === "ecommerce" && (
          <EcommerceMetrics ecommerce={data.ecommerce} loading={loading} screenAnalytics={data.analytics?.screenAnalytics} />
        )}
        {activeTab === "labBooking" && <LabBookingAnalytics />}

        {/* SCREENS TAB */}
        {activeTab === "screens" && <ScreenUserBreakdown range={range} />}
        {activeTab === "map" && <EventsGeoMapView range={range} />}
      </div>

      {/* USER JOURNEY MODAL */}
      {showUserJourney && (
        <UserJourneyModal userId={selectedUserId} onClose={closeUserJourney} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// CUSTOM DATE PICKER COMPONENT
// ============================================================================
const CustomDatePicker = ({
  customDates,
  setCustomDates,
  onClose,
  onApply,
}) => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }}
  >
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "32px",
        maxWidth: "500px",
        width: "90%",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        position: "relative",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#718096",
          padding: "8px",
        }}
      >
        <X size={24} />
      </button>

      <h2
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#1a202c",
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <Calendar size={28} color="#667eea" />
        Select Custom Date Range
      </h2>

      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#2d3748",
            marginBottom: "8px",
          }}
        >
          From Date
        </label>
        <input
          type="date"
          value={customDates.from}
          onChange={(e) =>
            setCustomDates({ ...customDates, from: e.target.value })
          }
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "14px",
            border: "2px solid #e2e8f0",
            borderRadius: "12px",
            outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#667eea")}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
      </div>

      <div style={{ marginBottom: "32px" }}>
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#2d3748",
            marginBottom: "8px",
          }}
        >
          To Date
        </label>
        <input
          type="date"
          value={customDates.to}
          onChange={(e) =>
            setCustomDates({ ...customDates, to: e.target.value })
          }
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "14px",
            border: "2px solid #e2e8f0",
            borderRadius: "12px",
            outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#667eea")}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
      </div>

      <div style={{ display: "flex", gap: "12px" }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: "14px 24px",
            fontSize: "14px",
            fontWeight: "600",
            border: "2px solid #e2e8f0",
            borderRadius: "12px",
            cursor: "pointer",
            backgroundColor: "#fff",
            color: "#4a5568",
            transition: "all 0.2s",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onApply}
          disabled={!customDates.from || !customDates.to}
          style={{
            flex: 1,
            padding: "14px 24px",
            fontSize: "14px",
            fontWeight: "600",
            border: "none",
            borderRadius: "12px",
            cursor:
              !customDates.from || !customDates.to ? "not-allowed" : "pointer",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            opacity: !customDates.from || !customDates.to ? 0.5 : 1,
            transition: "all 0.2s",
          }}
        >
          Apply Range
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// ENHANCED USER ACTIVITY TABLE WITH FILTERS, SEARCH, SORT, PAGINATION
// ============================================================================
const EnhancedUserActivityTable = ({
  users,
  loading,
  onUserClick,
  filters,
  onFilterChange,
  onResetFilters,
  pagination,
  onPageChange,
  showFilterPanel,
  setShowFilterPanel,
}) => {
  const sortOptions = [
    { value: "eventCount", label: "Total Events" },
    { value: "screenViews", label: "Screen Views" },
    { value: "productViews", label: "Product Views" },
    { value: "lastActive", label: "Last Active" },
    { value: "sessionCount", label: "Sessions" },
    { value: "screenCount", label: "Screens" },
  ];

  const screenOptions = [
    "Home",
    "Cart",
    "Payment Page",
    "Search Medicine",
    "Upload Prescription",
    "Order Via Prescription",
    "Pet Care",
    "OTC Products List",
    "Product Details",
    "Login",
    "Register",
    "Profile",
  ];

  return (
    <div>
      {/* FILTER HEADER */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "24px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* SEARCH */}
          <div style={{ flex: "1 1 300px", position: "relative" }}>
            <Search
              size={18}
              style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#718096",
              }}
            />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px 12px 48px",
                fontSize: "14px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>

          {/* SORT BY */}
          <div style={{ flex: "0 1 200px" }}>
            <select
              value={filters.sortBy}
              onChange={(e) => onFilterChange("sortBy", e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "14px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                cursor: "pointer",
                outline: "none",
                fontWeight: "600",
                color: "#2d3748",
              }}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* SORT ORDER */}
          <button
            onClick={() =>
              onFilterChange(
                "sortOrder",
                filters.sortOrder === "asc" ? "desc" : "asc"
              )
            }
            style={{
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#fff",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#2d3748",
              transition: "all 0.2s",
            }}
          >
            {filters.sortOrder === "asc" ? (
              <SortAsc size={18} />
            ) : (
              <SortDesc size={18} />
            )}
            {filters.sortOrder === "asc" ? "Ascending" : "Descending"}
          </button>

          {/* FILTER BUTTON */}
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            style={{
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: showFilterPanel
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "#fff",
              color: showFilterPanel ? "#fff" : "#2d3748",
              border: showFilterPanel ? "none" : "2px solid #e2e8f0",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "600",
              transition: "all 0.2s",
              boxShadow: showFilterPanel
                ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                : "none",
            }}
          >
            <Filter size={18} />
            Filters
          </button>

          {/* RESET BUTTON */}
          <button
            onClick={onResetFilters}
            style={{
              padding: "12px 20px",
              backgroundColor: "#fff",
              color: "#ed8936",
              border: "2px solid #ed8936",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
              transition: "all 0.2s",
            }}
          >
            Reset
          </button>
        </div>

        {/* ADVANCED FILTERS PANEL */}
        {showFilterPanel && (
          <div
            style={{
              marginTop: "20px",
              paddingTop: "20px",
              borderTop: "2px solid #f7fafc",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            {/* SCREEN FILTER */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#718096",
                  marginBottom: "8px",
                }}
              >
                Screen
              </label>
              <select
                value={filters.screen}
                onChange={(e) => onFilterChange("screen", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "13px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <option value="">All Screens</option>
                {screenOptions.map((screen) => (
                  <option key={screen} value={screen}>
                    {screen}
                  </option>
                ))}
              </select>
            </div>

            {/* MIN EVENTS */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#718096",
                  marginBottom: "8px",
                }}
              >
                Min Events
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g., 10"
                value={filters.minEvents}
                onChange={(e) => onFilterChange("minEvents", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "13px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  outline: "none",
                }}
              />
            </div>

            {/* MAX EVENTS */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#718096",
                  marginBottom: "8px",
                }}
              >
                Max Events
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g., 100"
                value={filters.maxEvents}
                onChange={(e) => onFilterChange("maxEvents", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "13px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  outline: "none",
                }}
              />
            </div>

            {/* MIN SCREEN VIEWS */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#718096",
                  marginBottom: "8px",
                }}
              >
                Min Screen Views
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g., 5"
                value={filters.minScreenViews}
                onChange={(e) =>
                  onFilterChange("minScreenViews", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "13px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  outline: "none",
                }}
              />
            </div>

            {/* MIN PRODUCT VIEWS */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#718096",
                  marginBottom: "8px",
                }}
              >
                Min Product Views
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g., 2"
                value={filters.minProductViews}
                onChange={(e) =>
                  onFilterChange("minProductViews", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "13px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  outline: "none",
                }}
              />
            </div>
          </div>
        )}

        {/* ACTIVE FILTER BADGES */}
        {(filters.search ||
          filters.screen ||
          filters.minEvents ||
          filters.maxEvents ||
          filters.minScreenViews ||
          filters.minProductViews) && (
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {filters.search && (
              <span style={filterBadgeStyle}>
                Search: "{filters.search}"
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("search", "")}
                />
              </span>
            )}
            {filters.screen && (
              <span style={filterBadgeStyle}>
                Screen: {filters.screen}
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("screen", "")}
                />
              </span>
            )}
            {filters.minEvents && (
              <span style={filterBadgeStyle}>
                Min Events: {filters.minEvents}
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("minEvents", "")}
                />
              </span>
            )}
            {filters.maxEvents && (
              <span style={filterBadgeStyle}>
                Max Events: {filters.maxEvents}
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("maxEvents", "")}
                />
              </span>
            )}
            {filters.minScreenViews && (
              <span style={filterBadgeStyle}>
                Min Screen Views: {filters.minScreenViews}
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("minScreenViews", "")}
                />
              </span>
            )}
            {filters.minProductViews && (
              <span style={filterBadgeStyle}>
                Min Product Views: {filters.minProductViews}
                <X
                  size={14}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                  onClick={() => onFilterChange("minProductViews", "")}
                />
              </span>
            )}
          </div>
        )}
      </div>

      {/* USER TABLE */}
      <Card
        title={`Active Users (${pagination?.totalRecords || 0} total)`}
        icon={Users}
        color="#667eea"
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f7fafc", textAlign: "left" }}>
                <th style={tableHeaderStyle}>User</th>
                <th style={tableHeaderStyle}>Phone</th>
                <th style={tableHeaderStyle}>Total Events</th>
                <th style={tableHeaderStyle}>Screen Views</th>
                <th style={tableHeaderStyle}>Product Views</th>
                <th style={tableHeaderStyle}>Screens</th>
                <th style={tableHeaderStyle}>Location</th>
                <th style={tableHeaderStyle}>Last Active</th>
                <th style={tableHeaderStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{ padding: "20px", textAlign: "center" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <RefreshCw size={20} className="spin" color="#667eea" />
                      <span>Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#a0aec0",
                    }}
                  >
                    <Users
                      size={48}
                      style={{ marginBottom: "12px", opacity: 0.5 }}
                    />
                    <p style={{ margin: 0 }}>No users found</p>
                  </td>
                </tr>
              ) : (
                users.map((user, idx) => (
                  <tr
                    key={user.userId || idx}
                    style={{
                      borderBottom: "1px solid #e2e8f0",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f7fafc")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <td style={tableCellStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#2d3748" }}>
                          {user.fullName || "Anonymous"}
                        </div>
                      </div>
                    </td>
                    <td style={tableCellStyle}>{user.phone || "N/A"}</td>
                    <td style={tableCellStyle}>
                      <span style={badgeStyle("#667eea")}>
                        {user.eventCount}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={badgeStyle("#48bb78")}>
                        {user.screenViews || 0}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={badgeStyle("#9f7aea")}>
                        {user.productViews || 0}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={badgeStyle("#ed8936")}>
                        {user.screenCount}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {user.location?.pincode ? (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "12px",
                          }}
                        >
                          <MapPin size={12} color="#9f7aea" />
                          {user.location.pincode}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: "12px", color: "#718096" }}>
                        {new Date(user.lastActive).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <button
                        onClick={() => onUserClick(user.userId)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 16px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(102, 126, 234, 0.5)";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 8px rgba(102, 126, 234, 0.3)";
                        }}
                      >
                        <Activity size={14} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination && pagination.totalPages > 1 && (
          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              backgroundColor: "#f7fafc",
              borderRadius: "12px",
            }}
          >
            <div
              style={{ fontSize: "14px", color: "#718096", fontWeight: "600" }}
            >
              Page {pagination.currentPage} of {pagination.totalPages}
              <span style={{ marginLeft: "12px", color: "#2d3748" }}>
                ({pagination.totalRecords} users)
              </span>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                style={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor:
                    pagination.currentPage === 1 ? "#e2e8f0" : "#fff",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  cursor:
                    pagination.currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                  color: pagination.currentPage === 1 ? "#a0aec0" : "#2d3748",
                  transition: "all 0.2s",
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                style={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor:
                    pagination.currentPage === pagination.totalPages
                      ? "#e2e8f0"
                      : "#fff",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  cursor:
                    pagination.currentPage === pagination.totalPages
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: "600",
                  fontSize: "13px",
                  color:
                    pagination.currentPage === pagination.totalPages
                      ? "#a0aec0"
                      : "#2d3748",
                  transition: "all 0.2s",
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// ============================================================================
// OVERVIEW TAB
// ============================================================================
const OverviewTab = ({ data, loading, maxValue }) => (
  <>
    {/* KPI CARDS */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: "20px",
        marginBottom: "32px",
      }}
    >
      <KpiCard
        icon={Users}
        title="Active Users"
        value={data.analytics?.metrics?.totalActiveUsers || 0}
        color="#667eea"
        loading={loading}
      />
      <KpiCard
        icon={BarChart3}
        title="Total Events"
        value={data.analytics?.metrics?.totalEvents || 0}
        color="#48bb78"
        loading={loading}
      />
      <KpiCard
        icon={TrendingUp}
        title="Avg Events/User"
        value={data.analytics?.metrics?.avgEventsPerUser || 0}
        color="#ed8936"
        loading={loading}
      />
      <KpiCard
        icon={Smartphone}
        title="Unique Screens"
        value={data.screenCount?.totalScreens || 0}
        color="#9f7aea"
        loading={loading}
      />
    </div>

    {/* CHARTS GRID */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
        gap: "24px",
      }}
    >
      {/* EVENTS TIMELINE */}
      <div style={{ gridColumn: "span 2", minWidth: 0 }}>
        <Card title="Events Timeline" icon={BarChart3} color="#667eea">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "16px",
              height: "300px",
              alignItems: "flex-end",
              padding: "20px 10px",
            }}
          >
            {loading ? (
              Array(7)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "80px",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "12px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  </div>
                ))
            ) : data.eventsSeries.length > 0 ? (
              data.eventsSeries.slice(-7).map((point) => (
                <div
                  key={point.date}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: `${Math.max(
                        (point.count / maxValue) * 250,
                        30
                      )}px`,
                      background:
                        "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                      transition: "transform 0.2s",
                      position: "relative",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                    title={`${point.count} events\n${point.uniqueUsers} users`}
                  />
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#718096",
                      fontWeight: "600",
                    }}
                  >
                    {new Date(point.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#2d3748",
                    }}
                  >
                    {point.count}
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  gridColumn: "span 7",
                  textAlign: "center",
                  color: "#a0aec0",
                  padding: "40px",
                }}
              >
                No data available
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* TOP SCREENS */}
      <Card title="Top Screens" icon={Eye} color="#48bb78">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading
            ? Skeleton(5)
            : data.analytics?.screenAnalytics
                ?.slice(0, 6)
                .map((item, i) => (
                  <ListItem
                    key={item.screen}
                    rank={i + 1}
                    label={
                      item.screen ? item.screen.split("_").join(" ") : "(empty)"
                    }
                    value={item.totalVisits}
                    subtitle={`${item.uniqueUsers} users`}
                    color="#48bb78"
                  />
                )) || <EmptyState />}
        </div>
      </Card>

      {/* EVENT TYPES */}
      <Card title="Event Types" icon={Zap} color="#ed8936">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading
            ? Skeleton(2)
            : data.eventsByType
                .slice(0, 2)
                .map((item, i) => (
                  <ListItem
                    key={item.eventType}
                    rank={i + 1}
                    label={
                      item.eventType === "screen_view"
                        ? "Screen View"
                        : "Product View"
                    }
                    value={item.count}
                    color={
                      item.eventType === "screen_view" ? "#ed8936" : "#9f7aea"
                    }
                  />
                )) || <EmptyState />}
        </div>
      </Card>

      {/* TOP LOCATIONS */}
      <Card title="Top Locations" icon={MapPin} color="#9f7aea">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading
            ? Skeleton(6)
            : data.topLocations
                .slice(0, 6)
                .map((item, i) => (
                  <ListItem
                    key={item.pincode}
                    rank={i + 1}
                    label={item.pincode}
                    value={item.events}
                    color="#9f7aea"
                  />
                )) || <EmptyState />}
        </div>
      </Card>

      {/* DEVICES */}
      <Card title="Device Analytics" icon={HardDrive} color="#38b2ac">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading
            ? Skeleton(4)
            : data.devices.slice(0, 5).map((device, i) => (
                <div
                  key={device._id}
                  style={{
                    padding: "12px",
                    backgroundColor: "#f7fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#38b2ac",
                          color: "#fff",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                        }}
                      >
                        {i + 1}
                      </span>
                      <span style={{ fontWeight: "600", color: "#2d3748" }}>
                        {device._id}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#2d3748",
                      }}
                    >
                      {device.totalCount}
                    </span>
                  </div>
                  {device.versions?.slice(0, 2).map((v) => (
                    <div
                      key={v.version}
                      style={{
                        fontSize: "12px",
                        color: "#718096",
                        marginLeft: "32px",
                      }}
                    >
                      v{v.version}: {v.count} events
                    </div>
                  ))}
                </div>
              )) || <EmptyState />}
        </div>
      </Card>
    </div>
  </>
);

// ============================================================================
// E-COMMERCE METRICS — Restructured with Screens
// ============================================================================
const ECOM_SCREENS = [
  { key: "Home", label: "Home", step: 1 },
  { key: "Search Medicine", label: "Search", step: 2 },
  { key: "Cart", label: "Cart", step: 3 },
  { key: "Payment Page", label: "Payment", step: 4 },
];

const EcomFunnelBar = ({ step, label, visits, users, maxVisits, color, dropPct }) => {
  const barW = maxVisits > 0 ? (visits / maxVisits) * 100 : 0;
  return (
    <div>
      {dropPct !== null && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2px 0", gap: "4px" }}>
          <TrendingDown size={12} color="#e53e3e" />
          <span style={{ fontSize: "11px", color: "#e53e3e", fontWeight: "600" }}>-{dropPct}% drop-off</span>
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px",
        backgroundColor: visits > 0 ? "#f7fafc" : "#fafafa",
        borderRadius: "10px", border: `1px solid ${visits > 0 ? "#e2e8f0" : "#f0f0f0"}`,
        opacity: visits > 0 ? 1 : 0.55,
      }}>
        <span style={{
          width: "26px", height: "26px", background: visits > 0 ? color : "#cbd5e0",
          color: "#fff", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "11px", fontWeight: "700", flexShrink: 0,
        }}>{step}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
            <span style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px" }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#718096", fontWeight: "600" }}>
                <Users size={11} />{users}
              </span>
              <span style={{ fontWeight: "700", color: "#2d3748", fontSize: "15px", fontVariantNumeric: "tabular-nums" }}>{visits.toLocaleString()}</span>
            </div>
          </div>
          <div style={{ width: "100%", height: "5px", backgroundColor: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: `${barW}%`, height: "100%", background: color, borderRadius: "3px", transition: "width 0.5s ease" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const EcommerceMetrics = ({ ecommerce, loading, screenAnalytics }) => {
  const ECOM_FUNNEL_COLORS = ["#667eea", "#764ba2", "#9f7aea", "#ed8936", "#48bb78"];

  // Build funnel from screen analytics
  const ecomScreenData = (screenAnalytics || []);
  const funnelData = ECOM_SCREENS.map((screen, idx) => {
    const d = ecomScreenData.find((s) => s.screen === screen.key);
    const visits = d?.totalVisits || 0;
    const users = d?.uniqueUsers || 0;
    const maxVisits = ecomScreenData.length > 0 ? Math.max(...ECOM_SCREENS.map((es) => {
      const sd = ecomScreenData.find((s) => s.screen === es.key);
      return sd?.totalVisits || 0;
    }), 1) : 1;
    const prev = idx > 0 ? (ecomScreenData.find((s) => s.screen === ECOM_SCREENS[idx - 1].key)?.totalVisits || 0) : 0;
    const dropPct = idx > 0 && prev > 0 ? (((prev - visits) / prev) * 100).toFixed(1) : null;
    return { ...screen, visits, users, maxVisits, dropPct };
  });

  if (loading) {
    return (
      <div style={{ padding: "60px", textAlign: "center" }}>
        <RefreshCw size={40} className="spin" color="#667eea" />
        <p style={{ marginTop: "16px", color: "#718096" }}>Loading e-commerce metrics...</p>
      </div>
    );
  }

  return (
    <>
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "28px" }}>
        <KpiCard icon={ShoppingCart} title="Total Carts" value={ecommerce?.cart?.totalCarts || 0} color="#ed8936" />
        <KpiCard icon={CreditCard} title="Payments Initiated" value={ecommerce?.payment?.totalPayments || 0} subtitle={`₹${(ecommerce?.payment?.totalAmount || 0).toLocaleString()}`} color="#48bb78" />
        <KpiCard icon={TrendingUp} title="Conversion Rate" value={`${ecommerce?.conversionRate || 0}%`} color="#667eea" />
        <KpiCard icon={Package} title="Products Viewed" value={ecommerce?.topProducts?.length || 0} color="#9f7aea" />
      </div>

      {/* Row: Funnel + Top Products */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "28px" }}>
        {/* Screen Funnel */}
        <Card title="Shopping Funnel (App Screens)" icon={Monitor} color="#667eea">
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {funnelData.map((f, i) => (
              <EcomFunnelBar key={f.key} step={f.step} label={f.label} visits={f.visits} users={f.users} maxVisits={f.maxVisits} color={ECOM_FUNNEL_COLORS[i] || "#667eea"} dropPct={f.dropPct} />
            ))}
          </div>
        </Card>

        {/* Top Products */}
        <Card title="Top Viewed Products" icon={Package} color="#ed8936">
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "500px", overflowY: "auto" }}>
            {ecommerce?.topProducts?.length > 0 ? (
              ecommerce.topProducts.map((product, idx) => (
                <div key={product.product_id || idx} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px 14px", backgroundColor: "#f7fafc", borderRadius: "10px",
                  border: "1px solid #e2e8f0", transition: "all 0.2s",
                }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#ed893612"; e.currentTarget.style.borderColor = "#ed893630"; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#f7fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                    <span style={{
                      width: "28px", height: "28px", backgroundColor: "#ed8936", color: "#fff",
                      borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "12px", fontWeight: "700", flexShrink: 0,
                    }}>{idx + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "600", color: "#2d3748", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {product.product_name || "Unnamed Product"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                        <span style={badgeStyle("#ed8936")}>{product.type}</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#718096" }}>
                          <User size={11} />{product.uniqueUsers} users
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", marginLeft: "12px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "800", color: "#2d3748" }}>{product.views}</div>
                    <div style={{ fontSize: "11px", color: "#718096", fontWeight: "600" }}>views</div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

// ============================================================================
// SCREEN USER BREAKDOWN
// ============================================================================
const ScreenUserBreakdown = ({ range }) => {
  const [selectedScreen, setSelectedScreen] = useState("Home");
  const [screenUsers, setScreenUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const screens = [
    "Home",
    "Cart",
    "NuraHealthPackages",
    "Payment Page",
    "Search Medicine",
    "Upload Prescription",
    "Order Via Prescription",
    "Pet Care",
    "OTC Products List",
    "Product Details",
    "Login",
    "Register",
    "Profile",
    "Lab Test Home",
    "Lab Test Detail",
    "Lab Test Cart",
    "Lab Test Patient Details",
    "Lab Test Slot Booking",
    "Lab Test Booking Confirm",
  ];

  useEffect(() => {
    if (selectedScreen) {
      fetchScreenUsers();
    }
  }, [selectedScreen, range]);

  const fetchScreenUsers = async () => {
    setLoading(true);
  
    const screenToSend =
      selectedScreen === "NuraHealthPackages"
        ? "NovaHealthPackages"
        : selectedScreen;
  
    try {
      const params = new URLSearchParams({
        screen: screenToSend,
        range,
      });
  
      const response = await fetch(`${API_BASE}/users/by-screen?${params}`);
      const data = await response.json();
  
      setScreenUsers(data.data?.users || []);
    } catch (error) {
      console.error("Screen users error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Screen-wise User Tracking" icon={Eye} color="#9f7aea">
      {/* Screen Selector */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#2d3748",
            marginBottom: "8px",
            display: "block",
          }}
        >
          Select Screen
        </label>
        <select
          value={selectedScreen}
          onChange={(e) => setSelectedScreen(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "14px",
            border: "2px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            cursor: "pointer",
            outline: "none",
            fontWeight: "600",
            color: "#2d3748",
          }}
        >
          {screens.map((screen) => (
            <option key={screen} value={screen}>
              {screen}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "12px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#9f7aea20",
            borderRadius: "12px",
            textAlign: "center",
          }}
        >
          <div
            style={{ fontSize: "24px", fontWeight: "800", color: "#9f7aea" }}
          >
            {screenUsers.length}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#718096",
              fontWeight: "600",
              marginTop: "4px",
            }}
          >
            Total Users
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#48bb7820",
            borderRadius: "12px",
            textAlign: "center",
          }}
        >
          <div
            style={{ fontSize: "24px", fontWeight: "800", color: "#48bb78" }}
          >
            {screenUsers.reduce((sum, u) => sum + u.visitCount, 0)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#718096",
              fontWeight: "600",
              marginTop: "4px",
            }}
          >
            Total Visits
          </div>
        </div>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#ed893620",
            borderRadius: "12px",
            textAlign: "center",
          }}
        >
          <div
            style={{ fontSize: "24px", fontWeight: "800", color: "#ed8936" }}
          >
            {screenUsers.length > 0
              ? Math.round(
                  screenUsers.reduce((sum, u) => sum + u.visitCount, 0) /
                    screenUsers.length
                )
              : 0}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#718096",
              fontWeight: "600",
              marginTop: "4px",
            }}
          >
            Avg Visits/User
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div style={{ overflowX: "auto", maxHeight: "500px", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <tr style={{ backgroundColor: "#f7fafc" }}>
              <th style={tableHeaderStyle}>User</th>
              <th style={tableHeaderStyle}>Phone</th>
              <th style={tableHeaderStyle}>Pincode</th>
              <th style={tableHeaderStyle}>Visits</th>
              <th style={tableHeaderStyle}>First Visit</th>
              <th style={tableHeaderStyle}>Last Visit</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="5"
                  style={{ padding: "40px", textAlign: "center" }}
                >
                  <RefreshCw size={30} className="spin" color="#9f7aea" />
                </td>
              </tr>
            ) : screenUsers.length === 0 ? (
              <tr>
                <td
                  colSpan="5"
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#a0aec0",
                  }}
                >
                  No users found for this screen
                </td>
              </tr>
            ) : (
              screenUsers.map((user, idx) => (
                <tr
                  key={user.userId || idx}
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f7fafc")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <td style={tableCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div style={{ fontWeight: "600", color: "#2d3748" }}>
                        {user.fullName || "Anonymous"}
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>{user.phone || "N/A"}</td>
                  <td style={tableCellStyle}>
                    {user?.geoInfo?.pincode || "N/A"}
                  </td>

                  <td style={tableCellStyle}>
                    <span style={badgeStyle("#9f7aea")}>{user.visitCount}</span>
                  </td>
                  <td style={tableCellStyle}>
                    <span style={{ fontSize: "12px", color: "#718096" }}>
                      {new Date(user.firstVisit).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <span style={{ fontSize: "12px", color: "#718096" }}>
                      {new Date(user.lastVisit).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
const KpiCard = ({ icon: Icon, title, value, subtitle, color, loading }) => (
  <div
    style={{
      padding: "24px",
      backgroundColor: "#fff",
      borderRadius: "16px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s",
      cursor: "pointer",
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.1)";
      e.currentTarget.style.transform = "translateY(-4px)";
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.05)";
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <div
        style={{
          width: "56px",
          height: "56px",
          backgroundColor: `${color}20`,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={28} color={color} />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "13px",
            color: "#718096",
            fontWeight: "600",
            marginBottom: "4px",
          }}
        >
          {title}
        </div>
        {loading ? (
          <div
            style={{
              width: "80px",
              height: "28px",
              backgroundColor: "#e2e8f0",
              borderRadius: "6px",
              animation: "pulse 1.5s infinite",
            }}
          />
        ) : (
          <div
            style={{ fontSize: "28px", fontWeight: "800", color: "#1a202c" }}
          >
            {typeof value === "number" ? value.toLocaleString() : value}
          </div>
        )}
        {subtitle && (
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "4px" }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  </div>
);

const Card = ({ title, icon: Icon, color, children }) => (
  <div
    style={{
      backgroundColor: "#fff",
      borderRadius: "16px",
      padding: "24px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
      height: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "20px",
        paddingBottom: "16px",
        borderBottom: "2px solid #f7fafc",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          backgroundColor: `${color}20`,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={22} color={color} />
      </div>
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "700",
          color: "#1a202c",
          margin: 0,
        }}
      >
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const ListItem = ({ rank, label, value, subtitle, color }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px",
      backgroundColor: "#f7fafc",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      transition: "all 0.2s",
      cursor: "pointer",
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.backgroundColor = `${color}10`;
      e.currentTarget.style.borderColor = `${color}40`;
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.backgroundColor = "#f7fafc";
      e.currentTarget.style.borderColor = "#e2e8f0";
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <span
        style={{
          width: "28px",
          height: "28px",
          backgroundColor: color,
          color: "#fff",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          fontWeight: "700",
          flexShrink: 0,
        }}
      >
        {rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: "600",
            color: "#2d3748",
            fontSize: "14px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        {subtitle && (
          <div style={{ fontSize: "12px", color: "#718096", marginTop: "2px" }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
    <span
      style={{
        fontSize: "18px",
        fontWeight: "700",
        color: "#2d3748",
        marginLeft: "12px",
      }}
    >
      {typeof value === "number" ? value.toLocaleString() : value}
    </span>
  </div>
);

const Skeleton = (count) =>
  Array(count)
    .fill(0)
    .map((_, i) => (
      <div
        key={i}
        style={{
          padding: "12px",
          backgroundColor: "#f7fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "40px",
            backgroundColor: "#e2e8f0",
            borderRadius: "8px",
            animation: "pulse 1.5s infinite",
          }}
        />
      </div>
    ));

const EmptyState = () => (
  <div
    style={{
      textAlign: "center",
      color: "#a0aec0",
      padding: "40px",
      fontSize: "14px",
    }}
  >
    No data available
  </div>
);

// ============================================================================
// STYLES
// ============================================================================
const filterBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 12px",
  backgroundColor: "#667eea20",
  color: "#667eea",
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: "600",
};

const tableHeaderStyle = {
  padding: "12px 16px",
  fontSize: "13px",
  fontWeight: "700",
  color: "#4a5568",
  borderBottom: "2px solid #e2e8f0",
};

const tableCellStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  color: "#2d3748",
};

const badgeStyle = (color) => ({
  display: "inline-block",
  padding: "6px 12px",
  backgroundColor: `${color}20`,
  color: color,
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: "700",
});

export default Dashboard;
